import React from "react";
import type { Box } from "./frame";
import type { Theme } from "./theme";
import { Label } from "./annotate";

/**
 * Anivaze v4 — BIOLOGY actors (the fertilization video's cure).
 *
 * The reference study (design-plan/v4/REFERENCE-STUDY.md §3) pins our worst
 * failure: the sperm→egg→zygote→heartbeat script rendered as "orange dots on a
 * line". These actors DEPICT the noun and ANIMATE the one verb, all as a pure
 * function of `t` — deterministic, previewable, exportable, ~$0 (no image-gen):
 *
 *   Swarm      — many sperm swim toward a big egg; most fade, one breaks in,
 *                a fertilization flash locks the rest out.
 *   Division   — one zygote cleaves 1→2→4→8 cells on-beat (mitosis).
 *   Heartbeat  — the first heart: a heart shape thumping on a cardiac rhythm
 *                with a running ECG trace.
 *   Cell       — a single labeled cell (membrane + nucleus) for generic
 *                "cell / embryo / organism" beats.
 *
 * The Director drives it via items[0] = the subject, e.g. ["swarm"] — chosen by
 * parseBioConfig from the beat text, mirroring the engine actor's contract.
 */

const TAU = Math.PI * 2;
const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
// deterministic hash → [0,1)
const rnd = (i: number, s = 1) => {
  const x = Math.sin((i + 1) * 12.9898 * s + 78.233) * 43758.5453;
  return x - Math.floor(x);
};

export type BioSubject = "swarm" | "division" | "heartbeat" | "cell";

export interface BioConfig {
  subject: BioSubject;
}

/** Parse Director items / beat text into a biology subject. */
export function parseBioConfig(text: string): BioConfig {
  const s = text.toLowerCase();
  // VERBS outrank nouns: "the fertilized egg splits into two cells" is a
  // DIVISION beat, not a swarm beat — mirror the engine actor's stage-verb rule.
  const subject: BioSubject =
    /divid|division|\bsplit|cleav|mitosis|two cells|four cells|eight cells|blastocyst|2 ?cells|multipl/.test(s) ? "division"
    : /heart|heartbeat|beating|\bpulse|cardiac|\becg\b|ekg|pulsing/.test(s) ? "heartbeat"
    : /sperm|\begg\b|fertil|conception|fallopian|swim|swarm|ovum|reach the egg/.test(s) ? "swarm"
    : "cell";
  return { subject };
}

/** Biology-subject signal — a fertilization/cell beat must get a real actor. */
export const BIO_RE =
  /\bsperm\b|\begg cell\b|fertiliz|zygote|blastocyst|embryo|\bmitosis\b|cell divi|cells? split|\bheartbeat\b|first heart|\bplacenta\b|fallopian|conception|\bovum\b|nucleus fus/i;

interface VProps { items: string[]; box: Box; theme: Theme; unit: number; t: number }

// Leader-line labels (Rule 3: point at the noun) now come from ./annotate.

// ── Swarm: sperm race to the egg, one breaks in, the rest lock out ──────────
const Swarm: React.FC<VProps & { u: number }> = ({ box, theme, t, u }) => {
  const P = theme.palette;
  const eggR = 11 * u;
  const eggX = box.cx + 12 * u;
  const eggY = box.cy;
  // fertilization moment — paced over ~3s (t is seconds), so the race PLAYS OUT
  // instead of finishing in the first frame; losers keep milling (never frozen).
  const fertT = 3.2;
  const fert = clamp01((t - fertT) / 0.6);
  const winnerReach = clamp01(t / fertT); // winner travels to the egg by fertT

  const N = 26;
  const sperm = Array.from({ length: N }, (_, i) => {
    const isWinner = i === 0;
    const startX = box.x + 2 * u + rnd(i, 3) * 6 * u;
    const startY = box.y + box.h * (0.12 + 0.76 * rnd(i, 7));
    const wob = Math.sin(t * TAU * 2 + i) * 1.4 * u; // tail wiggle sway (continuous)
    // winner heads straight to egg; losers drift, fall behind, and after fert
    // hang back milling near the egg — dimmed but still swimming (motion never stops)
    const prog = isWinner
      ? easeInOut(winnerReach)
      : easeInOut(clamp01(t / (3 + rnd(i, 5) * 2))) * (0.5 + 0.38 * rnd(i, 2));
    const tx = eggX - eggR - 2 * u;
    const x = lerp(startX, tx, prog);
    const y = lerp(startY, eggY, prog) + wob;
    const fade = isWinner ? 1 : Math.max(0.28, 1 - fert * 0.7);
    return { x, y, i, isWinner, fade, phase: t * TAU * 3 + i };
  });

  return (
    <g>
      {/* egg */}
      <circle cx={eggX} cy={eggY} r={eggR + 2.4 * u} fill="none" stroke={P.highlight} strokeWidth={u * 0.7} opacity={0.5} />
      <circle cx={eggX} cy={eggY} r={eggR} fill={theme.dark ? "#e9d9a8" : "#f3e6b8"} stroke={P.highlight} strokeWidth={u * 0.5} />
      <circle cx={eggX - eggR * 0.25} cy={eggY - eggR * 0.2} r={eggR * 0.4} fill={P.primary} opacity={0.85} />
      {/* fertilization flash + hardened zona (lock-out ring) */}
      {fert > 0 && (
        <circle cx={eggX} cy={eggY} r={(eggR + 2.4 * u) * (1 + 0.3 * fert)} fill="none"
          stroke={P.danger} strokeWidth={u * (1 - 0.5 * fert)} opacity={0.9 * (1 - fert)} />
      )}
      {/* sperm */}
      {sperm.map((s) => (
        <g key={s.i} opacity={Math.max(0, s.fade)} transform={`translate(${s.x} ${s.y})`}>
          <path d={`M0,0 Q${-2.2 * u},${Math.sin(s.phase) * 1.3 * u} ${-4.4 * u},${Math.sin(s.phase + 1) * 1.6 * u}`}
            fill="none" stroke={s.isWinner ? P.danger : P.sub} strokeWidth={u * 0.35} />
          <circle r={u * (s.isWinner ? 1.15 : 0.9)} fill={s.isWinner ? P.danger : P.secondary} />
        </g>
      ))}
      <Label tx={eggX + eggR} ty={eggY - eggR * 0.6} lx={eggX + eggR + 6 * u} ly={eggY - eggR} text="Egg" theme={theme} u={u} />
      <Label tx={box.x + 5 * u} ty={box.y + box.h * 0.86} lx={box.x + 6 * u} ly={box.y + box.h * 0.95} text="Sperm" theme={theme} u={u} />
    </g>
  );
};

// ── Division: 1 → 2 → 4 → 8 cells (mitosis on-beat) ─────────────────────────
const Division: React.FC<VProps & { u: number }> = ({ box, theme, t, u }) => {
  const P = theme.palette;
  // four stages divided over the first ~3.2s (t is seconds), so the cleavage
  // PLAYS OUT rather than snapping to 8 in the first frame.
  const DIV = 3.2;
  const stages = [1, 2, 4, 8];
  const fstage = clamp01(t / DIV) * (stages.length - 1e-6);
  const si = Math.min(stages.length - 1, Math.floor(fstage));
  const frac = easeInOut(fstage - si);
  const count = stages[si];
  const next = stages[Math.min(stages.length - 1, si + 1)];
  // once settled, the whole cluster drifts slowly + cells breathe — never frozen
  const spin = t * 5; // deg/sec, gentle
  const breathe = (i: number) => 1 + 0.05 * Math.sin(t * 2.2 + i);

  const R = 9 * u; // cluster radius envelope
  const cx = box.cx, cy = box.cy;
  // lay `n` cells on a tightening ring; each cell shrinks as count grows
  const cellR = (n: number) => Math.max(3 * u, 10 * u / Math.sqrt(n));
  const layout = (n: number) =>
    Array.from({ length: n }, (_, i) => {
      if (n === 1) return { x: cx, y: cy };
      const a = (i / n) * TAU - Math.PI / 2;
      const ring = n <= 2 ? R * 0.5 : R;
      return { x: cx + Math.cos(a) * ring, y: cy + Math.sin(a) * ring };
    });

  const from = layout(count);
  const to = layout(next);
  const rNow = lerp(cellR(count), cellR(next), frac);
  // interpolate positions: each current cell drifts toward its two daughters' midpoint
  const cells = to.map((d, i) => {
    const parent = from[i % count];
    return { x: lerp(parent.x, d.x, frac), y: lerp(parent.y, d.y, frac), i };
  });

  return (
    <g>
      <g transform={`rotate(${spin} ${cx} ${cy})`}>
        {cells.map((c) => (
          <g key={c.i}>
            <circle cx={c.x} cy={c.y} r={rNow * breathe(c.i)} fill={theme.dark ? "#2e4a52" : "#cfe8ee"} stroke={P.primary} strokeWidth={u * 0.5} />
            <circle cx={c.x} cy={c.y} r={rNow * 0.42} fill={P.primary} opacity={0.85} />
          </g>
        ))}
      </g>
      <text x={cx} y={box.y + box.h * 0.9} fill={P.ink} fontSize={u * 2} fontWeight={800}
        textAnchor="middle" fontFamily="system-ui, sans-serif">
        {`${count} cell${count > 1 ? "s" : ""}`}
      </text>
      <Label tx={cx + R} ty={cy - R} lx={cx + R + 5 * u} ly={cy - R - 2 * u} text="Zygote" theme={theme} u={u} on={si === 0 ? 1 : 0.25} />
    </g>
  );
};

// ── Heartbeat: the first heart thumping + ECG trace ─────────────────────────
const heartPath = (cx: number, cy: number, s: number) =>
  `M ${cx} ${cy + 7 * s}
   C ${cx - 9 * s} ${cy - 1 * s}, ${cx - 8 * s} ${cy - 9 * s}, ${cx} ${cy - 4 * s}
   C ${cx + 8 * s} ${cy - 9 * s}, ${cx + 9 * s} ${cy - 1 * s}, ${cx} ${cy + 7 * s} Z`;

// ECG height (in `y` units) at cycle-local position `local∈[0,1)`.
const ecgWave = (local: number) =>
  local > 0.02 && local < 0.06 ? -1
  : local >= 0.06 && local < 0.12 ? 5      // R spike
  : local >= 0.12 && local < 0.16 ? -2.4   // S
  : local >= 0.2 && local < 0.28 ? 1.2     // T
  : 0;

const Heartbeat: React.FC<VProps & { u: number }> = ({ box, theme, t, u }) => {
  const P = theme.palette;
  const cx = box.cx - 6 * u, cy = box.cy;
  // ~84 bpm cardiac double-thump (t is seconds, so this beats continuously)
  const beatHz = 1.4;
  const ph = (t * beatHz) % 1;
  const thump = ph < 0.12 ? ph / 0.12 : ph < 0.24 ? 1 - (ph - 0.12) / 0.12 * 0.6 : ph < 0.34 ? 0.4 + (ph - 0.24) / 0.1 * 0.4 : Math.max(0, 0.8 - (ph - 0.34) / 0.2);
  const scale = u * (1 + 0.14 * thump);

  // ECG trace: a static waveform of a few complexes, always drawn, with a
  // bright scan dot sweeping left→right on a loop (a live heart monitor).
  const ecgY = cy + 16 * u;
  const ecgX0 = box.x + 3 * u, ecgX1 = box.x + box.w - 3 * u;
  const cycles = 4;
  const M = 120;
  const pts: string[] = [];
  for (let k = 0; k <= M; k++) {
    const f = k / M;
    const x = lerp(ecgX0, ecgX1, f);
    pts.push(`${x},${ecgY - ecgWave((f * cycles) % 1) * 2.2 * u}`);
  }
  const scanF = (t / 2.4) % 1; // full sweep every 2.4s
  const scanX = lerp(ecgX0, ecgX1, scanF);
  const scanY = ecgY - ecgWave((scanF * cycles) % 1) * 2.2 * u;

  return (
    <g>
      <path d={heartPath(cx, cy, scale)} fill={P.danger} stroke={theme.dark ? "#ff8a8a" : "#a01722"} strokeWidth={u * 0.5} />
      {/* pulse halo on the thump */}
      <path d={heartPath(cx, cy, scale)} fill="none" stroke={P.danger} strokeWidth={u * 0.6}
        opacity={0.35 * thump} transform={`translate(${cx} ${cy}) scale(${1 + 0.25 * thump}) translate(${-cx} ${-cy})`} />
      <polyline points={pts.join(" ")} fill="none" stroke={P.positive} strokeWidth={u * 0.4} opacity={0.5} />
      <circle cx={scanX} cy={scanY} r={u * 0.7} fill={P.positive} />
      <Label tx={cx} ty={cy - 5 * u} lx={cx + 11 * u} ly={cy - 10 * u} text="First heart" theme={theme} u={u} />
    </g>
  );
};

// ── Cell: a single labeled cell (membrane + nucleus) ────────────────────────
const Cell: React.FC<VProps & { u: number }> = ({ box, theme, t, u }) => {
  const P = theme.palette;
  const cx = box.cx, cy = box.cy;
  const R = 13 * u * (0.9 + 0.1 * Math.sin(t * TAU)); // gentle breathe
  return (
    <g>
      <circle cx={cx} cy={cy} r={R} fill={theme.dark ? "#2e4a52" : "#d6edf1"} stroke={P.primary} strokeWidth={u * 0.6} />
      <circle cx={cx} cy={cy} r={R * 0.4} fill={P.primary} opacity={0.85} />
      <circle cx={cx} cy={cy} r={R * 0.4} fill="none" stroke={theme.dark ? "#0b2b33" : "#ffffff"} strokeWidth={u * 0.3} opacity={0.5} />
      {/* organelles */}
      {Array.from({ length: 5 }, (_, i) => {
        const a = t * TAU * 0.15 + (i / 5) * TAU;
        const rr = R * 0.66;
        return <ellipse key={i} cx={cx + Math.cos(a) * rr} cy={cy + Math.sin(a) * rr} rx={u * 1.4} ry={u * 0.9}
          fill={P.secondary} opacity={0.8} transform={`rotate(${(a * 180) / Math.PI} ${cx + Math.cos(a) * rr} ${cy + Math.sin(a) * rr})`} />;
      })}
      <Label tx={cx + R * 0.28} ty={cy} lx={cx + R + 5 * u} ly={cy - u} text="Nucleus" theme={theme} u={u} />
      <Label tx={cx + R * 0.92} ty={cy + R * 0.38} lx={cx + R + 5 * u} ly={cy + 4 * u} text="Membrane" theme={theme} u={u} />
    </g>
  );
};

export const BioActor: React.FC<VProps> = (props) => {
  const { box } = props;
  const cfg = parseBioConfig(props.items.join(" "));
  const u = Math.min(box.h / 36, box.w / 34);
  switch (cfg.subject) {
    case "swarm": return <Swarm {...props} u={u} />;
    case "division": return <Division {...props} u={u} />;
    case "heartbeat": return <Heartbeat {...props} u={u} />;
    default: return <Cell {...props} u={u} />;
  }
};
